// src/views/LenderLibrary.jsx
// LENDER LIBRARY — Reece's bank of lender material (guidelines, bulletins, rate
// sheets, matrices, FAQs, AE emails) plus "Ask the Library": a chat that
// answers from those pages the way a good AE would — conversationally — with
// the pages behind every answer one tap away.
//
// Contract: docs/lender-library-design.md ("UI"). Data layer is src/kb.js;
// the server side is the kb-ask / kb-ocr actions on api/anthropic-proxy.js.
//
//   Ask      The chat. ONE conversation per person, the same one the floating
//            chat bubble (ChatDock) shows: the thread, a running answer, the
//            draft and the lender chips live in the store in
//            src/libraryChat.js, and both render it with the shared
//            components in src/views/ChatThread.jsx (answer card, Sources,
//            cost, Dig deeper, Copy, Try again, the deal-basics chip). This
//            tab adds the lender chips, the empty-library states and the page
//            viewer: a cite chip or a source opens the page image, the quoted
//            passage, and the original file at that page.
//   Library  Drag-and-drop ingest (one file at a time — and it keeps going if
//            he leaves this screen), paste-an-email notes, the documents table,
//            and a full-text "search inside documents".
//
// Model output is rendered as TEXT only (pre-wrap) — never as HTML.

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { C, Ic } from '../theme';
import { Btn, Inp, Sel, Badge, Hdr } from '../ui';
import { KB_LENDERS, KB_DOC_TYPES, KB_RATE_SHEET, KB_CHANGED_EVENT, olderRateSheets, listDocuments, ingestFile, retryIngest, addTextNote, updateDocument, deleteDocument, searchPages, getSignedUrl, renderPageImage, getPageText, docKind, officeKind } from '../kb';
import { asksForSources, getChatStore, useLibraryChat } from '../libraryChat';
import { ChatThread, ChatComposer } from './ChatThread';

const FONT="'Mulish',system-ui,sans-serif";
const PASTE_KEY="lf_kb_paste_draft_v1"; // per-device draft of the paste-text box
const ACCEPT=".pdf,.png,.jpg,.jpeg,.webp,.txt,.md,.csv,.docx,.xlsx";
const OK_EXT=new Set(["pdf","png","jpg","jpeg","webp","txt","md","csv","docx","xlsx"]);
const NOTE_SAVE_MS=700;           // notes autosave this long after the last keystroke
const DOCS_STALE_MS=60e3;         // coming back to the Library after this long re-reads the documents list
const IMG_CACHE_MAX=12;           // rendered page images kept; blob URLs revoked on eviction
const LINK_STALE_MS=50*60e3;      // signed URLs live 60 min — refresh after 50
// A running ingest touches its row about once a minute (kb.js heartbeat), so a
// row still 'processing' with no touch for this long lost its browser tab.
const STALE_MS=15*60e3;
const EXAMPLES=[
  "What's the minimum age for HomeSafe in Florida?",
  "How is a non-borrowing spouse handled on a HECM?",
  "What are the current Longbridge pricing incentives?",
  "Does SmartFi allow a condo that isn't FHA-approved?",
];

// ═══════ helpers ═══════
const uid=function(){return Date.now().toString(36)+Math.random().toString(36).slice(2,8);};
const errMsg=function(e,fb){return (e&&e.message)||fb||"unknown error";};
const extOf=function(n){const m=/\.([a-z0-9]+)$/i.exec(String(n||""));return m?m[1].toLowerCase():"";};
const baseName=function(n){return String(n||"").replace(/\.[a-z0-9]+$/i,"").trim()||"Untitled";};
// "2026-09-29" → "9/29/26" (string math — no Date(), so no timezone drift)
const fmtEff=function(d){const m=/^(\d{4})-(\d{2})-(\d{2})/.exec(String(d||""));return m?(+m[2])+"/"+(+m[3])+"/"+m[1].slice(2):"";};
const fmtSize=function(b){const n=Number(b)||0;if(n<1024)return n+" B";if(n<1048576)return Math.round(n/1024)+" KB";return (n/1048576).toFixed(n<10485760?1:0)+" MB";};
const fmtDay=function(ts){if(!ts)return"";const d=new Date(ts);return Number.isNaN(d.getTime())?"":d.toLocaleDateString();};
const parsePrograms=function(s){const out=[];String(s||"").split(",").forEach(function(x){const t=x.trim();if(t&&out.indexOf(t)<0)out.push(t);});return out;};
const isPdf=function(doc){return !!doc&&(/pdf/i.test(doc.mime||"")||/\.pdf$/i.test(doc.file_name||"")||/\.pdf$/i.test(doc.file_path||""));};
const docMeta=function(d){return {title:d.title,lender:d.lender,docType:d.doc_type,effectiveDate:d.effective_date,superseded:!!d.superseded};};

// Same order listDocuments returns: lender, newest effective date (blank last), title.
function sortDocs(list){
  return list.slice().sort(function(x,y){
    const l=String(x.lender||"").localeCompare(String(y.lender||""));if(l)return l;
    const ex=x.effective_date||"",ey=y.effective_date||"";
    if(ex!==ey){if(!ex)return 1;if(!ey)return -1;return ex<ey?1:-1;}
    return String(x.title||"").localeCompare(String(y.title||""));
  });
}
function upsertDoc(prev,row){const list=(prev||[]).filter(function(d){return d.id!==row.id;});list.push(row);return sortDocs(list);}

// Words worth highlighting from a search query (websearch syntax: quotes, OR, -exclusions).
const STOP=new Set(["and","the","for","with","not","are","was","from","that","this","what","how","does","or"]);
function termsOf(q){
  const out=[];
  String(q||"").toLowerCase().replace(/["()]/g," ").split(/\s+/).forEach(function(raw){
    if(!raw||raw.charAt(0)==="-")return;
    const w=raw.replace(/^[^a-z0-9$]+|[^a-z0-9%]+$/g,"");
    if((w.length>=3||/^\d{2,}$/.test(w))&&!STOP.has(w)&&out.indexOf(w)<0)out.push(w);
  });
  return out.slice(0,8);
}
// ~len-char window around the first matching term.
function snippetOf(text,terms,len){
  const L=len||200;
  const t=String(text||"").replace(/\s+/g," ").trim();
  if(!t)return "";
  const low=t.toLowerCase();
  let at=-1;
  (terms||[]).forEach(function(w){const i=low.indexOf(w);if(i>=0&&(at<0||i<at))at=i;});
  if(at<0)return t.length>L?t.slice(0,L).trim()+"…":t;
  const end=Math.min(t.length,Math.max(0,at-60)+L);
  const start=Math.max(0,end-L);
  return (start>0?"…":"")+t.slice(start,end).trim()+(end<t.length?"…":"");
}

// Text with matched words marked — built as React nodes, never HTML.
function Highlight({text,terms}){
  const s=String(text||"");
  if(!terms||!terms.length||!s)return s;
  const re=new RegExp("("+terms.map(function(w){return w.replace(/[.*+?^${}()|[\]\\]/g,"\\$&");}).join("|")+")","ig");
  return s.split(re).map(function(p,i){
    return i%2===1?<mark key={i} style={{background:C.claySoft,color:C.text,borderRadius:3,padding:"0 1px"}}>{p}</mark>:<React.Fragment key={i}>{p}</React.Fragment>;
  });
}
// Where a cited passage sits in its page's text: exact first, then ignoring
// whitespace differences. Citations are verbatim spans, but the server clips
// them to 400 chars with a trailing "…", and a search snippet starts with "…"
// when its match is deeper than ~60 characters into the page.
function findQuote(s,quote){
  const q=String(quote||"").replace(/^…|…$/g,"").trim();
  if(q.length<4)return null;
  const i=s.indexOf(q);
  if(i>=0)return [i,i+q.length];
  const words=q.split(/\s+/).filter(Boolean).slice(0,40);
  if(words.length<3)return null;
  try{
    const m=new RegExp(words.map(function(w){return w.replace(/[.*+?^${}()|[\]\\]/g,"\\$&");}).join("\\s+")).exec(s);
    return m?[m.index,m.index+m[0].length]:null;
  }catch(e){return null;}
}
// A page's full text with the cited passage marked (and scrolled to) and any
// search terms highlighted — React nodes, never HTML.
function MarkedText({text,quote,terms}){
  const s=String(text||"");
  const span=useMemo(function(){return findQuote(s,quote);},[s,quote]);
  const ref=useRef(null);
  useEffect(function(){const el=ref.current;if(el&&el.scrollIntoView)el.scrollIntoView({block:"nearest"});},[span]);
  const st={fontSize:12.5,lineHeight:1.6,color:C.text,whiteSpace:"pre-wrap",overflowWrap:"anywhere"};
  if(!span)return <div style={st}><Highlight text={s} terms={terms}/></div>;
  return(<div style={st}>
    <Highlight text={s.slice(0,span[0])} terms={terms}/>
    <mark ref={ref} style={{background:"rgba(201,123,92,0.28)",color:C.text,borderRadius:3,boxShadow:"0 0 0 2px rgba(201,123,92,0.28)"}}>{s.slice(span[0],span[1])}</mark>
    <Highlight text={s.slice(span[1])} terms={terms}/>
  </div>);
}

function docsErrText(e){
  const s=String((e&&e.message)||"").trim();
  const code=String((e&&e.code)||"");
  return {setup:code==="42P01"||code==="PGRST205"||/isn't set up|does not exist|schema cache/i.test(s),text:s||"Couldn't load your library."};
}

// The paste-text box's draft (an AE email half pasted, its title, notes)
// survives a reload or a sign-out on this device until it's saved or closed.
const PASTE_FIELDS=["title","lender","doc_type","programs","effective_date","notes","text"];
function pasteHasDraft(p){return !!(p&&(String(p.text||"").trim()||String(p.title||"").trim()||String(p.notes||"").trim()));}
function loadPasteDraft(base){
  try{
    const o=JSON.parse(localStorage.getItem(PASTE_KEY)||"null");
    if(!o||typeof o!=="object")return base;
    const p=Object.assign({},base);
    PASTE_FIELDS.forEach(function(k){if(typeof o[k]==="string")p[k]=o[k];});
    if(KB_LENDERS.indexOf(p.lender)<0)p.lender="";
    if(KB_DOC_TYPES.indexOf(p.doc_type)<0)p.doc_type=base.doc_type;
    p.open=pasteHasDraft(p);
    return p;
  }catch(e){return base;}
}
function savePasteDraft(p){
  try{
    if(!pasteHasDraft(p)){localStorage.removeItem(PASTE_KEY);return;}
    const o={};PASTE_FIELDS.forEach(function(k){o[k]=String(p[k]||"");});
    localStorage.setItem(PASTE_KEY,JSON.stringify(o));
  }catch(e){/* storage full / blocked — the draft just isn't kept */}
}

function useNarrow(px){
  const get=function(){return typeof window!=="undefined"&&window.innerWidth<px;};
  const [n,setN]=useState(get);
  useEffect(function(){
    const on=function(){setN(get());};
    window.addEventListener("resize",on);on();
    return function(){window.removeEventListener("resize",on);};
  },[px]);// eslint-disable-line react-hooks/exhaustive-deps
  return n;
}

// ═══════ Ingest queue ═══════
// Module-level on purpose: a 40-file upload with scanned pages takes a while,
// and switching screens must not stop it or lose the list. ONE file runs at a
// time; the mounted Library view subscribes for re-renders. Closing the
// browser tab mid-queue gets a "leave site?" prompt.
// Item: {qid, file (the picked File — null for a stored document being picked
// up again), name, size, meta, state: waiting|running|error|done, progress,
// error, doc, docId (its kb_documents row, once known), resume (a row to pick
// up via retryIngest instead of starting fresh), supersede ([{id,title,lender}]
// — older rate sheets to mark superseded once this one is in), note/noteErr
// (how that went)}.
const IQ={items:[],running:false,subs:new Set(),onDoc:null};
function iqEmit(){const snap=IQ.items.slice();IQ.subs.forEach(function(f){try{f(snap);}catch(e){/* view gone */}});}
function iqPatch(qid,p){IQ.items=IQ.items.map(function(it){return it.qid===qid?Object.assign({},it,p):it;});iqEmit();}
function iqWarnUnload(e){e.preventDefault();e.returnValue="";return "";}
function iqAdd(list){IQ.items=IQ.items.concat(list);iqEmit();iqPump();}
function iqRemove(qid){IQ.items=IQ.items.filter(function(it){return it.qid!==qid||it.state==="running";});iqEmit();}
function iqClearDone(){IQ.items=IQ.items.filter(function(it){return it.state!=="done";});iqEmit();}
async function iqPump(){
  if(IQ.running)return;
  const it=IQ.items.find(function(x){return x.state==="waiting";});
  if(!it){window.removeEventListener("beforeunload",iqWarnUnload);return;}
  IQ.running=true;
  window.addEventListener("beforeunload",iqWarnUnload);
  iqPatch(it.qid,{state:"running",error:"",progress:{stage:"upload"}});
  const onProgress=function(p){
    if(!p||typeof p!=="object")return;
    const patch={progress:Object.assign({},p)};
    if(p.docId)patch.docId=p.docId;
    iqPatch(it.qid,patch);
  };
  try{
    let row;
    if(it.resume){
      // Pick up where it stopped: the record and every saved page are kept,
      // only the missing part runs. Record deleted since → start this file over.
      try{row=await retryIngest(it.resume,it.file,onProgress);}
      catch(e){if(e&&e.code==="doc_gone"&&it.file)row=await ingestFile(it.file,it.meta,onProgress);else throw e;}
    }else row=await ingestFile(it.file,it.meta,onProgress);
    const ok=!(row&&row.status==="error");
    if(!ok)iqPatch(it.qid,{state:"error",error:String(row.error||"Couldn't read this file."),resume:row});
    else iqPatch(it.qid,{state:"done",doc:row||null,resume:null,progress:{stage:"done"}});
    if(IQ.onDoc)IQ.onDoc(row||null);
    if(ok&&row&&it.supersede&&it.supersede.length){try{await iqSupersede(it,row);}catch(e){/* never fails the upload */}}
  }catch(e){
    // A failure after the record was made leaves it at 'error' (err.doc), with
    // every page saved so far — Retry resumes that record rather than
    // stacking a duplicate. (A failed "Retry pages" on a ready document
    // leaves it ready, so the same document is retried again.)
    const leftover=e&&e.doc&&e.doc.id?e.doc:null;
    const gone=!!(e&&e.code==="doc_gone");
    iqPatch(it.qid,{state:"error",error:errMsg(e,"Something went wrong adding this file."),resume:leftover||(gone?null:it.resume)||null});
    if(IQ.onDoc)IQ.onDoc(leftover);
  }finally{
    IQ.running=false;
  }
  iqPump();
}
// A new rate sheet is in: mark the older sheets picked at upload time (same
// lender, older effective date) superseded. Never fails the upload — how it
// went is a note on the item, and each updated row flows to the table.
async function iqSupersede(it,row){
  const list=(it.supersede||[]).filter(function(s){return s&&s.id&&s.id!==row.id;});
  if(!list.length)return;
  let done=0;const errs=[];
  for(let i=0;i<list.length;i++){
    try{
      const r=await updateDocument(list[i].id,{superseded:true});done++;
      if(IQ.onDoc&&r&&r.id)IQ.onDoc(r);
    }catch(e){errs.push("“"+(list[i].title||"older sheet")+"”: "+errMsg(e));}
  }
  const who=list[0].lender?list[0].lender+" ":"";
  const note=errs.length
    ?"Couldn't mark "+(errs.length===1?"an older ":errs.length+" older ")+who+"rate sheet"+(errs.length===1?"":"s")+" superseded — "+errs[0]+(done?" ("+done+" other"+(done===1?" was":"s were")+" marked)":"")
    :(done===1?"Older "+who+"rate sheet marked superseded.":done+" older "+who+"rate sheets marked superseded.");
  iqPatch(it.qid,{supersede:null,note:note,noteErr:errs.length>0});
}
function iqRetry(qid){
  const it=IQ.items.find(function(x){return x.qid===qid;});
  if(!it||it.state!=="error")return;
  iqPatch(qid,{state:"waiting",error:"",progress:null});
  iqPump();
}
// A stored document that didn't finish (error, a stalled upload, or ready
// with untranscribed pages) → back into the queue to be picked up. Reuses a
// failed queue entry for it when there is one (that entry still has the File).
function iqResumeDoc(d){
  const same=IQ.items.find(function(x){return x.docId===d.id&&x.state!=="done";});
  if(same){if(same.state==="error")iqRetry(same.qid);return;}
  iqAdd([{qid:uid(),file:null,name:d.file_name||"",size:Number(d.size_bytes)||0,state:"waiting",progress:null,error:"",resume:d,docId:d.id,
    meta:{title:d.title||"Untitled",lender:d.lender||""}}]);
}
const qName=function(it){return it.file?it.file.name:(it.name||"");};
const qSize=function(it){return it.file?it.file.size:(it.size||0);};
// kb.js sends a ready-made line with each progress event ("Reading text: page
// 3 of 40", "Transcribing scanned pages: 2 of 5 (page 14)") — prefer it.
function stageText(it){
  const p=it.progress||{};
  if(it.state==="waiting")return it.resume?"Waiting in line — will pick up where it stopped":"Waiting in line";
  if(it.state==="error")return it.error||"Couldn't add this file.";
  if(it.state==="done"){const n=it.doc&&it.doc.page_count;return "Ready"+(n?" — "+n+" page"+(n===1?"":"s")+" indexed":"");}
  if(p.message)return p.message;
  switch(p.stage){
    case "upload":return "Uploading the file…";
    case "extract":return p.pages?"Reading text: page "+(p.page||0)+" of "+p.pages:"Reading text…";
    case "ocr":return p.pages?"Transcribing scanned pages: "+(p.page||0)+" of "+p.pages:"Transcribing scanned pages with AI…";
    case "save":return "Saving pages to the library…";
    default:return "Working…";
  }
}
// Rough overall bar: upload → read text → save the pages → transcribe any scans
// (each transcribed page is saved as it lands).
function pctOf(it){
  const p=it.progress||{};
  const frac=p.pages?Math.min(1,(p.page||0)/p.pages):0;
  if(p.stage==="upload")return 5;
  if(p.stage==="extract")return 8+Math.round(47*frac);
  if(p.stage==="save")return 55+Math.round(5*frac);
  if(p.stage==="ocr")return 60+Math.round(38*frac);
  if(p.stage==="done")return 100;
  return 3;
}

// ═══════ small UI atoms ═══════
const card={background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:"14px 16px",marginBottom:16};
const secTitle={fontSize:13.5,fontWeight:800,color:C.text};
const label11={fontSize:10.5,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.7};
const taStyle={width:"100%",minHeight:80,padding:"10px 12px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:13.5,fontFamily:FONT,resize:"vertical",outline:"none",boxSizing:"border-box",lineHeight:1.5};
const linkBtn={background:"none",border:"none",padding:0,cursor:"pointer",fontFamily:FONT,color:C.accentLight,fontWeight:800,fontSize:12};
const aBtn={display:"inline-flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:8,background:"linear-gradient(135deg,"+C.accent+","+C.accentDark+")",color:"#fff",fontSize:12,fontWeight:700,textDecoration:"none",fontFamily:FONT,minHeight:36,boxSizing:"border-box",boxShadow:"0 1px 3px rgba(11,42,74,0.30)"};
const errBox={padding:"10px 14px",background:C.redBg,color:C.red,borderRadius:8,fontSize:12.5,fontWeight:600,lineHeight:1.5};
const chipStyle=function(on,dim){return {padding:"6px 12px",borderRadius:20,cursor:"pointer",fontSize:12,fontWeight:800,fontFamily:FONT,border:"1px solid "+(on?C.accent:C.border),background:on?C.accent:C.card,color:on?"#fff":(dim?C.textDim:C.textMuted),whiteSpace:"nowrap"};};
const LENDER_OPTS=[{value:"",label:"Choose lender…"}].concat(KB_LENDERS.map(function(l){return {value:l,label:l};}));
const TYPE_OPTS=KB_DOC_TYPES.map(function(t){return {value:t,label:t};});

const Spinner=function({size,color}){const s=size||14;return <span aria-hidden="true" style={{display:"inline-block",width:s,height:s,border:"2px solid "+(color||C.accent),borderTopColor:"transparent",borderRadius:"50%",animation:"lf-kb-spin 0.8s linear infinite",flexShrink:0,verticalAlign:"middle",boxSizing:"border-box"}}/>;};
function Switch({on,onClick,disabled,title}){
  return(<button type="button" role="switch" aria-checked={!!on} aria-label={title} title={title} onClick={onClick} disabled={disabled}
    style={{width:36,height:20,borderRadius:10,border:"none",padding:2,background:on?C.orange:C.cream2,display:"inline-flex",alignItems:"center",justifyContent:on?"flex-end":"flex-start",cursor:disabled?"not-allowed":"pointer",flexShrink:0,opacity:disabled?.5:1}}>
    <span style={{width:16,height:16,borderRadius:8,background:"#fff",boxShadow:"0 1px 2px rgba(6,26,48,0.3)"}}/>
  </button>);
}
// Column flex so a <select> stretches to the full field width.
function Field({label,req,hint,children}){
  return(<label style={{display:"flex",flexDirection:"column",minWidth:0}}>
    <span style={{...label11,marginBottom:5}}>{label}{req&&<span style={{color:C.clay}}> *</span>}</span>
    {children}
    {hint&&<span style={{fontSize:11,color:C.textDim,marginTop:4}}>{hint}</span>}
  </label>);
}
function Modal({title,onClose,children,width}){
  useEffect(function(){
    const k=function(e){if(e.key==="Escape"&&onClose)onClose();};
    window.addEventListener("keydown",k);
    return function(){window.removeEventListener("keydown",k);};
  },[onClose]);
  return(<div onClick={function(){if(onClose)onClose();}} style={{position:"fixed",inset:0,zIndex:320,background:"rgba(6,26,48,0.5)",display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"40px 14px",overflowY:"auto"}}>
    <div onClick={function(e){e.stopPropagation();}} role="dialog" aria-modal="true" aria-label={title} style={{background:C.card,borderRadius:14,border:"1px solid "+C.border,width:width||520,maxWidth:"100%",padding:"20px 22px",boxShadow:"0 18px 60px rgba(6,26,48,0.35)",boxSizing:"border-box"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
        <div style={{fontSize:17,fontWeight:800,color:C.text,flex:1,minWidth:0,overflowWrap:"anywhere"}}>{title}</div>
        {onClose&&<button onClick={onClose} aria-label="Close" style={{background:"none",border:"none",fontSize:26,lineHeight:1,cursor:"pointer",color:C.textDim}}>×</button>}
      </div>
      {children}
    </div>
  </div>);
}

// ═══════ Page viewer (side panel) ═══════
// The "where did this come from" view: the cited passage, the page itself,
// and the original file opened at that page.
function PageViewer({v,doc,docsLoading,onClose,setPage,getPageImage,dropPageImage,getLink,narrow}){
  const page=Math.max(1,v.page||1);
  const total=doc?(Number(doc.page_count)||0):0;
  const meta=doc?docMeta(doc):(v.meta||{});
  const key=doc?doc.id+":"+page:"";
  const hasFile=!!(doc&&doc.file_path);
  const pdf=isPdf(doc);
  const isNote=!!doc&&!doc.file_path;
  // Pasted notes and .txt/.md/.csv files have no page picture — the page IS
  // its stored text, so that's what the viewer shows (cited span marked).
  const textDoc=isNote||(!!doc&&docKind(doc)==="text");
  const [img,setImg]=useState({url:null,loading:true,err:""});
  const [nonce,setNonce]=useState(0);
  const [link,setLink]=useState({url:"",at:0,err:"",loading:false});
  const [zoom,setZoom]=useState(false);
  const [textOpen,setTextOpen]=useState(false);
  const [ptext,setPtext]=useState({key:"",text:"",loading:false,err:""});
  const [textNonce,setTextNonce]=useState(0);
  const showText=textDoc||textOpen;
  const hash=pdf?"#page="+page:"";

  // Page image (PDF → rendered canvas; image doc → its signed URL; text → none).
  useEffect(function(){
    if(!doc||!doc.file_path||textDoc){setImg({url:null,loading:false,err:""});return;}
    let live=true;
    setImg({url:null,loading:true,err:""});
    getPageImage(doc,page).then(function(url){
      if(!live)return;
      setImg({url:url||null,loading:false,err:""});
      // warm the next page so "page ▶" is instant
      if(url&&pdf&&page<total)getPageImage(doc,page+1).catch(function(){});
    },function(e){if(live)setImg({url:null,loading:false,err:errMsg(e,"Couldn't draw this page.")});});
    return function(){live=false;};
  },[key,nonce,hasFile,textDoc]);// eslint-disable-line react-hooks/exhaustive-deps

  // The page's stored text (what search and the chatbot read) — always for
  // text documents, on request for the rest. A search hit already carries it.
  useEffect(function(){
    if(!doc||!showText)return;
    if(v.pageText&&v.pageTextPage===page){setPtext({key:key,text:String(v.pageText),loading:false,err:""});return;}
    let live=true;
    setPtext({key:key,text:"",loading:true,err:""});
    getPageText(doc,page).then(function(t){if(live)setPtext({key:key,text:String(t||""),loading:false,err:""});},
      function(e){if(live)setPtext({key:key,text:"",loading:false,err:errMsg(e,"Couldn't load this page's text.")});});
    return function(){live=false;};
  },[key,showText,textNonce]);// eslint-disable-line react-hooks/exhaustive-deps

  // Signed link to the original file — fetched up front so the click is a
  // real <a target=_blank> (popup blockers allow it).
  useEffect(function(){
    if(!doc||!doc.file_path){setLink({url:"",at:0,err:"",loading:false});return;}
    let live=true;
    setLink({url:"",at:0,err:"",loading:true});
    getLink(doc).then(function(o){if(live)setLink({url:o.url,at:o.at,err:"",loading:false});},
      function(e){if(live)setLink({url:"",at:0,err:errMsg(e,"Couldn't make a link."),loading:false});});
    return function(){live=false;};
  },[doc&&doc.id,doc&&doc.file_path]);// eslint-disable-line react-hooks/exhaustive-deps

  const refreshLink=function(andOpen){
    setLink(function(l){return Object.assign({},l,{loading:true,err:""});});
    getLink(doc,true).then(function(o){
      setLink({url:o.url,at:o.at,err:"",loading:false});
      if(andOpen)window.open(o.url+hash,"_blank","noopener,noreferrer");
    },function(e){setLink({url:"",at:0,err:errMsg(e,"Couldn't make a link."),loading:false});});
  };
  // Signed URLs expire after an hour — if this panel sat open that long, get a fresh one.
  const openOriginal=function(e){
    if(link.url&&Date.now()-link.at<LINK_STALE_MS)return;
    e.preventDefault();refreshLink(true);
  };
  const retryImg=function(){if(doc)dropPageImage(doc,page);setNonce(function(n){return n+1;});};

  // Esc closes; ← / → turn pages (unless typing somewhere).
  useEffect(function(){
    const onKey=function(e){
      if(e.key==="Escape"){onClose();return;}
      const tag=(e.target&&e.target.tagName)||"";
      if(/^(INPUT|TEXTAREA|SELECT)$/.test(tag))return;
      if(e.key==="ArrowLeft"&&page>1)setPage(page-1);
      else if(e.key==="ArrowRight"&&total&&page<total)setPage(page+1);
    };
    window.addEventListener("keydown",onKey);
    return function(){window.removeEventListener("keydown",onKey);};
  },[page,total,onClose,setPage]);
  // Keep the page behind from scrolling under the panel.
  useEffect(function(){
    const prev=document.body.style.overflow;document.body.style.overflow="hidden";
    return function(){document.body.style.overflow=prev;};
  },[]);

  const quoteHere=!!v.quote&&(v.quotePage==null||v.quotePage===page);
  // The page's text, the cited span marked — shared by text documents and the
  // "Show this page's text" toggle.
  let textBody=null;
  if(showText){
    textBody=(ptext.key!==key||ptext.loading)
      ?<div style={{display:"flex",alignItems:"center",gap:10,color:C.textMuted,fontSize:13,padding:"6px 2px"}}><Spinner/>Loading the text of page {page}…</div>
      :ptext.err
        ?<div style={{fontSize:12.5,color:C.red,fontWeight:600,lineHeight:1.5,overflowWrap:"anywhere"}}>{ptext.err} <button onClick={function(){setTextNonce(function(n){return n+1;});}} style={linkBtn}>Try again</button></div>
        :ptext.text.trim()
          ?<MarkedText text={ptext.text} quote={quoteHere?v.quote:""} terms={v.terms}/>
          :<div style={{fontSize:13,color:C.textDim}}>This page has no text.</div>;
  }

  let openEl=null;
  if(doc&&!hasFile)openEl=<span style={{fontSize:11.5,color:C.textDim}}>Pasted text — no original file</span>;
  else if(doc&&link.url)openEl=<a href={link.url+hash} target="_blank" rel="noopener noreferrer" onClick={openOriginal} style={aBtn}>{pdf?"Open original at this page ↗":"Open original ↗"}</a>;
  else if(doc&&link.err)openEl=<span style={{fontSize:11.5,color:C.red,fontWeight:600}}>Couldn't make a link to the original. <button onClick={function(){refreshLink(false);}} style={linkBtn}>Try again</button></span>;
  else if(doc)openEl=<span style={{fontSize:11.5,color:C.textDim,display:"inline-flex",alignItems:"center",gap:6}}><Spinner size={12}/>Preparing link…</span>;

  return(<div onClick={onClose} style={{position:"fixed",inset:0,zIndex:300,background:"rgba(6,26,48,0.45)",display:"flex",justifyContent:"flex-end"}}>
    <aside onClick={function(e){e.stopPropagation();}} role="dialog" aria-modal="true" aria-label="Page viewer"
      style={{width:narrow?"100%":"min(780px,94vw)",height:"100%",background:C.bg,borderLeft:"1px solid "+C.border,boxShadow:"-2px 0 40px rgba(6,26,48,0.25)",display:"flex",flexDirection:"column",animation:"lf-kb-slide .2s cubic-bezier(.2,.8,.2,1)",fontFamily:FONT,boxSizing:"border-box"}}>
      {/* ── header: what document, which page, how to get to the original ── */}
      <div style={{padding:narrow?"12px 14px":"14px 20px",borderBottom:"1px solid "+C.border,background:C.card}}>
        <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
          <span style={{color:C.accentLight,marginTop:2,flexShrink:0,display:"flex"}}>{Ic.book}</span>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:15,fontWeight:800,color:C.text,lineHeight:1.3,overflowWrap:"anywhere"}}>{meta.title||"Document"}</div>
            <div style={{fontSize:12,color:C.textMuted,marginTop:3,display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
              <span>{[meta.lender,meta.docType,meta.effectiveDate?"eff. "+fmtEff(meta.effectiveDate):""].filter(Boolean).join(" · ")}</span>
              {meta.superseded&&<Badge label="superseded" color={C.orange} bg={C.orangeBg}/>}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close page viewer" style={{width:32,height:32,borderRadius:8,border:"1px solid "+C.border,background:"transparent",color:C.textMuted,cursor:"pointer",flexShrink:0,fontSize:14}}>✕</button>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginTop:10}}>
          {total>1&&<Btn small onClick={function(){setPage(page-1);}} disabled={page<=1}>◀ page</Btn>}
          <span style={{fontSize:12.5,fontWeight:800,color:C.text,whiteSpace:"nowrap"}}>Page {page}{total?" of "+total:""}</span>
          {total>1&&<Btn small onClick={function(){setPage(page+1);}} disabled={page>=total}>page ▶</Btn>}
          <span style={{flex:1}}/>
          {img.url&&<Btn small onClick={function(){setZoom(!zoom);}}>{zoom?"Fit to width":"Zoom in"}</Btn>}
          {openEl}
        </div>
      </div>

      {/* ── body: quote first (that's the point), then the page ── */}
      <div style={{flex:1,overflowY:"auto",padding:narrow?"12px 14px 24px":"16px 20px 28px",display:"flex",flexDirection:"column",gap:12}}>
        {quoteHere&&<div style={{borderLeft:"4px solid "+C.clay,background:"rgba(201,123,92,0.10)",borderRadius:"0 10px 10px 0",padding:"12px 14px"}}>
          <div style={{...label11,color:C.clayDeep,marginBottom:6}}>{v.quoteLabel||"Quoted passage"} · p.{page}</div>
          <div style={{fontSize:13.5,lineHeight:1.6,color:C.text,whiteSpace:"pre-wrap",overflowWrap:"anywhere"}}>“<Highlight text={v.quote} terms={v.terms}/>”</div>
        </div>}
        {v.quote&&!quoteHere&&<div style={{fontSize:12,color:C.textDim}}>The quoted passage is on page {v.quotePage}. <button onClick={function(){setPage(v.quotePage);}} style={linkBtn}>Back to page {v.quotePage}</button></div>}

        {!doc?(docsLoading
          ?<div style={{display:"flex",alignItems:"center",gap:10,color:C.textMuted,fontSize:13,padding:"20px 4px"}}><Spinner/>Loading your library…</div>
          :<div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:"18px 16px",fontSize:13,color:C.textMuted,lineHeight:1.55}}>This document isn't in your library anymore — it may have been deleted. {v.quote?"The quote above is the part the answer used.":""}</div>)
        :textDoc
          ?<div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:"14px 16px"}}>
            <div style={{...label11,marginBottom:8}}>{isNote?"Pasted text":officeKind(doc)?(/xls|sheet|excel/i.test(String(doc.file_name||"")+String(doc.mime||""))?"Spreadsheet text":"Word document text"):"Text file"} — page {page}{total?" of "+total:""}</div>
            {textBody}
          </div>
          :<div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,overflow:"auto",minHeight:220,maxHeight:zoom?"78vh":"none",display:"flex",alignItems:img.url?"flex-start":"center",justifyContent:(img.url&&zoom)?"flex-start":"center"}}>
            {img.loading
              ?<div style={{display:"flex",alignItems:"center",gap:10,color:C.textMuted,fontSize:13,padding:20}}><Spinner/>Loading page {page}…</div>
              :img.err
                ?<div style={{padding:20,textAlign:"center",fontSize:13,color:C.textMuted,lineHeight:1.5}}>
                  <div style={{color:C.red,fontWeight:700,marginBottom:6}}>Couldn't show this page.</div>
                  <div style={{marginBottom:10,overflowWrap:"anywhere"}}>{img.err}</div>
                  <Btn small onClick={retryImg}>Try again</Btn>
                </div>
                :img.url
                  ?<img src={img.url} alt={"Page "+page+" of "+(meta.title||"document")}
                    onError={function(){setImg({url:null,loading:false,err:"The page image didn't load."});}}
                    onClick={function(){setZoom(!zoom);}}
                    style={{display:"block",width:zoom?"auto":"100%",maxWidth:zoom?"none":"100%",height:"auto",cursor:zoom?"zoom-out":"zoom-in"}}/>
                  :<div style={{padding:20,fontSize:13,color:C.textMuted}}>No page image is available for this document.</div>}
          </div>}

        {doc&&!textDoc&&<div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:"10px 14px"}}>
          <button onClick={function(){setTextOpen(!textOpen);}} style={linkBtn}>{textOpen?"Hide":"Show"} this page's text {textOpen?"▾":"▸"}</button>
          {textOpen&&<div style={{marginTop:8,maxHeight:360,overflowY:"auto"}}>{textBody}</div>}
        </div>}
      </div>
    </aside>
  </div>);
}

// ═══════ Ask tab ═══════
// The thread, the answer being written, the draft and the lender chips come
// from the store (src/libraryChat.js) — the chat bubble on every other
// screen shows the same conversation. `shown`: this tab is on screen (the
// Library view is visible AND the Ask tab is picked). App keeps the Library
// mounted but hidden on other screens; hidden, the answer being written isn't
// drawn (it catches up when shown), and an answer that landed while no chat
// was on screen is tagged and scrolled to when it's back.
function AskTab({staff,docs,onOpen,activeSrc,goLibrary,shown,readOnly}){
  const store=getChatStore(staff);
  const turnCount=useLibraryChat(staff,function(s){return s.turns.length;});
  const busy=useLibraryChat(staff,function(s){return s.busy;});
  const filters=useLibraryChat(staff,function(s){return s.filters;});
  const fresh=useLibraryChat(staff,function(s){return s.fresh;});
  const taRef=useRef(null);
  const endRef=useRef(null);
  const composerRef=useRef(null);

  // The Whiteboard bubble (bottom-left, views/WhiteboardDock.jsx) sits just
  // above this page's Ask box — which sticks to the bottom of the screen and
  // grows with the question (up to ~250px) — so it never covers the start of
  // the lines being typed: the box's height goes out as --lf-ask-h.
  useEffect(function(){
    if(!shown)return undefined;
    const el=composerRef.current;
    const root=typeof document!=="undefined"?document.documentElement:null;
    if(!el||!root||!root.style)return undefined;
    const pub=function(){const h=el.offsetHeight||0;if(h>0)root.style.setProperty("--lf-ask-h",Math.ceil(h)+"px");};
    pub();
    let ro=null;
    try{if(typeof ResizeObserver==="function"){ro=new ResizeObserver(pub);ro.observe(el);}}catch(e){ro=null;}
    return function(){if(ro)ro.disconnect();root.style.removeProperty("--lf-ask-h");};
  },[shown]);

  // An answer that lands while this is on screen was watched — no "new" tag.
  useEffect(function(){
    store.setViewing("library",!!shown);
    return function(){store.setViewing("library",false);};
  },[store,shown]);
  // Back on screen: bring an answer that finished while away into view (once).
  useEffect(function(){
    if(!shown||!fresh||fresh.seen)return;
    const id=fresh.id;
    const raf=requestAnimationFrame(function(){
      const el=document.getElementById("kb-turn-"+id);
      if(el&&el.offsetParent!==null&&el.scrollIntoView)el.scrollIntoView({block:"start"});
      store.markSeen("library");
    });
    return function(){cancelAnimationFrame(raf);};
  },[shown,fresh,store]);

  const stats=useMemo(function(){
    const o={total:0,ready:0,processing:0,error:0,byLender:{}};
    (docs||[]).forEach(function(d){
      o.total++;
      if(d.status==="ready"){o.ready++;if(filters.includeSuperseded||!d.superseded)o.byLender[d.lender]=(o.byLender[d.lender]||0)+1;}
      else if(d.status==="error")o.error++;
      else o.processing++;
    });
    return o;
  },[docs,filters.includeSuperseded]);
  const docsLoaded=Array.isArray(docs);
  const noReady=docsLoaded&&stats.ready===0;

  // Follow the answer as it streams — only if he's already at the bottom.
  const follow=useCallback(function(){
    const el=endRef.current;
    if(!el||el.offsetParent===null)return;
    const doc=document.documentElement;
    if(window.innerHeight+window.scrollY>=doc.scrollHeight-260)el.scrollIntoView({block:"end"});
  },[]);
  const onSent=useCallback(function(){
    setTimeout(function(){const el=endRef.current;if(el&&el.offsetParent!==null)el.scrollIntoView({block:"end",behavior:"smooth"});},30);
  },[]);
  const openSrc=useCallback(function(t,s){
    onOpen({docId:s.docId,page:s.page,quote:s.citedText,quotePage:s.page,quoteLabel:"Quoted in the answer as ["+s.n+"]",srcKey:t.id+":"+s.n,
      meta:{title:s.title,lender:s.lender,docType:s.docType,effectiveDate:s.effectiveDate,superseded:s.superseded}});
  },[onOpen]);

  const newChat=function(){
    if(turnCount&&!window.confirm("Start a new chat? This clears the conversation — here and in the chat bubble — on this device."))return;
    store.newChat();
  };
  const toggleLender=function(l){
    store.setFilters(function(f){const has=f.lenders.indexOf(l)>=0;return Object.assign({},f,{lenders:has?f.lenders.filter(function(x){return x!==l;}):f.lenders.concat([l])});});
  };
  const pickExample=function(q){store.setDraft(q);setTimeout(function(){if(taRef.current)taRef.current.focus();},0);};

  return(<div>
    {/* ── filters ── */}
    <div style={{...card,padding:"12px 14px"}}>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
        <span style={{...label11,marginRight:4}}>Lenders</span>
        <button onClick={function(){store.setFilters(function(f){return Object.assign({},f,{lenders:[]});});}} style={chipStyle(!filters.lenders.length)}>All lenders</button>
        {KB_LENDERS.map(function(l){const on=filters.lenders.indexOf(l)>=0;const n=stats.byLender[l]||0;return(
          <button key={l} onClick={function(){toggleLender(l);}} aria-pressed={on} style={chipStyle(on,!n&&!on)}>{l} <span style={{opacity:.7,fontWeight:700}}>{docsLoaded?n:""}</span></button>);})}
      </div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginTop:10}}>
        <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12.5,fontWeight:700,color:C.textMuted,cursor:"pointer"}}>
          <input type="checkbox" checked={filters.includeSuperseded} onChange={function(e){const v=e.target.checked;store.setFilters(function(f){return Object.assign({},f,{includeSuperseded:v});});}} style={{width:16,height:16,cursor:"pointer"}}/>
          Include superseded documents
        </label>
        <span style={{flex:1}}/>
        {docsLoaded&&<span style={{fontSize:12,color:C.textDim}}>{stats.ready} document{stats.ready===1?"":"s"} ready{stats.processing?" · "+stats.processing+" processing":""}</span>}
        <Btn small onClick={newChat} disabled={!turnCount&&!busy}>New chat</Btn>
      </div>
    </div>

    {/* ── empty / welcome states ── */}
    {noReady&&docs.length===0&&<div style={{...card,padding:"22px 20px",textAlign:"center"}}>
      <div style={{color:C.clay,display:"flex",justifyContent:"center",marginBottom:8}}>{Ic.book}</div>
      <div style={{fontSize:15,fontWeight:800,color:C.text}}>Your library is empty</div>
      <div style={{fontSize:13,color:C.textMuted,marginTop:6,lineHeight:1.55,maxWidth:520,marginLeft:"auto",marginRight:"auto"}}>{readOnly
        ?"No lender documents have been added yet. Once Reece adds guidelines, bulletins or rate sheets, ask anything here — the pages behind every answer are one tap away."
        :"Upload lender guidelines, program bulletins, rate sheets, matrices or AE emails in the Library tab. Then ask anything here — the pages behind every answer are one tap away."}</div>
      {!readOnly&&<div style={{marginTop:14,display:"flex",justifyContent:"center"}}><Btn primary onClick={goLibrary}>Go to Library →</Btn></div>}
    </div>}
    {noReady&&docs.length>0&&<div style={{padding:"12px 14px",background:C.orangeBg,border:"1px solid "+C.orange+"40",borderRadius:10,fontSize:12.5,color:C.text,marginBottom:16,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",lineHeight:1.5}}>
      <span style={{flex:"1 1 240px"}}>None of your documents are ready to search yet{stats.processing?" — "+stats.processing+" still processing":""}{stats.error?(stats.processing?", ":" — ")+stats.error+" had errors":""}.</span>
      <Btn small onClick={goLibrary}>Open Library</Btn>
    </div>}
    {!noReady&&turnCount===0&&!busy&&<div style={{...card,padding:"18px 20px"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,fontSize:14.5,fontWeight:800,color:C.text}}><span style={{color:C.clay,display:"flex"}}>{Ic.book}</span>Ask your lender library</div>
      <div style={{fontSize:13,color:C.textMuted,marginTop:6,lineHeight:1.55}}>Ask like you'd ask your AE. Answers come from your uploaded documents{docsLoaded?" ("+stats.ready+" ready)":""} — tap Sources under any answer, or ask “where does it say that?”, to see the pages.</div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:12}}>
        {EXAMPLES.map(function(q){return <button key={q} onClick={function(){pickExample(q);}} style={{...chipStyle(false),whiteSpace:"normal",textAlign:"left",fontWeight:700}}>{q}</button>;})}
      </div>
    </div>}

    {/* ── thread (shared with the chat bubble) ── */}
    <ChatThread staff={staff} activeSrc={activeSrc} onOpenSrc={openSrc} goLibrary={goLibrary} showFresh liveShown={!!shown} onUpdate={follow}/>
    <div ref={endRef} style={{height:1,scrollMarginBottom:140}}/>

    {/* ── composer (sticks to the bottom of the screen) ── */}
    <div ref={composerRef} data-ask-composer="" style={{position:"sticky",bottom:0,zIndex:5,background:"linear-gradient(180deg,rgba(250,248,242,0) 0,"+C.bg+" 14px)",paddingTop:14,paddingBottom:10,marginTop:6}}>
      <ChatComposer staff={staff} inputRef={taRef} disabled={noReady} onSent={onSent} ariaLabel="Ask the library"
        placeholder={noReady?(readOnly?"No documents are ready to search yet…":"Add documents in the Library tab first…"):"Ask about guidelines, ages, limits, overlays, pricing…"}/>
    </div>
  </div>);
}

// ═══════ Library tab ═══════
// The other fields wait for "Save changes"; Notes saves itself — ~0.7 s after
// the last keystroke, on leaving the box, and on closing (or unmounting)
// the dialog, whichever comes first.
function EditDocModal({doc,shown,onClose,onSaved,onPatched}){
  const [d,setD]=useState({title:doc.title||"",lender:doc.lender||"Other",doc_type:doc.doc_type||"Guideline",programs:(doc.programs||[]).join(", "),
    effective_date:String(doc.effective_date||"").slice(0,10),superseded:!!doc.superseded,notes:doc.notes||""});
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  const [noteSt,setNoteSt]=useState({s:"",err:""});   // s: "" | saving | saved | error
  const set=function(p){setD(function(x){return Object.assign({},x,p);});};
  // cur = what's typed; saved = what the row holds; sending = in flight now
  const nRef=useRef({cur:String(doc.notes||""),saved:String(doc.notes||"").trim(),sending:null,timer:0,dead:false});
  const patchedRef=useRef(onPatched);patchedRef.current=onPatched;
  const flushNotes=useRef(null);
  flushNotes.current=function(){
    const n=nRef.current;
    if(n.timer){clearTimeout(n.timer);n.timer=0;}
    const v=n.cur.trim();
    if(v===n.saved||v===n.sending)return;
    n.sending=v;
    if(!n.dead)setNoteSt({s:"saving",err:""});
    updateDocument(doc.id,{notes:v}).then(function(row){
      if(n.sending===v)n.sending=null;
      n.saved=v;
      if(patchedRef.current)patchedRef.current(row&&row.id?row:Object.assign({},doc,{notes:v}));
      if(!n.dead)setNoteSt(function(st){return n.cur.trim()===v?{s:"saved",err:""}:st;});
    },function(e){
      if(n.sending===v)n.sending=null;
      if(!n.dead)setNoteSt({s:"error",err:errMsg(e,"Couldn't save the notes.")});
    });
  };
  useEffect(function(){
    const n=nRef.current;
    return function(){n.dead=true;flushNotes.current();};
  },[]);
  const onNotes=function(e){
    const v=e.target.value;const n=nRef.current;
    n.cur=v;set({notes:v});
    if(n.timer)clearTimeout(n.timer);
    n.timer=setTimeout(function(){n.timer=0;flushNotes.current();},NOTE_SAVE_MS);
  };
  const close=function(){flushNotes.current();onClose();};
  const save=async function(){
    if(!d.title.trim()){setErr("Give the document a title.");return;}
    setBusy(true);setErr("");
    const patch={title:d.title.trim(),lender:d.lender,doc_type:d.doc_type,programs:parsePrograms(d.programs),effective_date:d.effective_date||null,superseded:d.superseded,notes:d.notes.trim()};
    const n=nRef.current;
    if(n.timer){clearTimeout(n.timer);n.timer=0;}
    try{
      const row=await updateDocument(doc.id,patch);
      n.saved=patch.notes;
      onSaved(row&&row.id?row:Object.assign({},doc,patch));
    }catch(e){setBusy(false);setErr(errMsg(e,"Couldn't save the changes."));}
  };
  const noteHint=noteSt.s==="saving"?"Saving…":noteSt.s==="saved"?"✓ Saved":noteSt.s==="error"?"Not saved — "+noteSt.err:"Saves as you type";
  return(<Modal title="Edit document" onClose={busy||shown===false?null:close} width={600}>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
      <div style={{gridColumn:"1 / -1"}}><Field label="Title" req><Inp value={d.title} onChange={function(e){set({title:e.target.value});}}/></Field></div>
      <Field label="Lender" req><Sel value={d.lender} onChange={function(e){set({lender:e.target.value});}} options={LENDER_OPTS.slice(1)}/></Field>
      <Field label="Document type"><Sel value={d.doc_type} onChange={function(e){set({doc_type:e.target.value});}} options={TYPE_OPTS}/></Field>
      <Field label="Programs" hint="Comma-separated"><Inp value={d.programs} onChange={function(e){set({programs:e.target.value});}} placeholder="HomeSafe Standard, HECM"/></Field>
      <Field label="Effective date"><Inp type="date" value={d.effective_date} onChange={function(e){set({effective_date:e.target.value});}}/></Field>
    </div>
    <label style={{display:"flex",alignItems:"center",gap:8,marginTop:14,fontSize:13,fontWeight:700,color:d.superseded?C.orange:C.textMuted,cursor:"pointer"}}>
      <input type="checkbox" checked={d.superseded} onChange={function(e){set({superseded:e.target.checked});}} style={{width:16,height:16,cursor:"pointer"}}/>
      Superseded — a newer version replaces this (answers skip it unless "Include superseded" is on)
    </label>
    <div style={{marginTop:12}}>
      <Field label="Notes"><textarea value={d.notes} onChange={onNotes} onBlur={function(){flushNotes.current();}} style={{...taStyle,minHeight:70}}/></Field>
      <div aria-live="polite" style={{fontSize:11,marginTop:4,fontWeight:noteSt.s?700:500,color:noteSt.s==="error"?C.red:noteSt.s==="saved"?C.green:C.textDim,overflowWrap:"anywhere"}}>{noteHint}</div>
    </div>
    {err&&<div style={{...errBox,marginTop:12}}>{err}</div>}
    <div style={{display:"flex",gap:8,justifyContent:"flex-end",flexWrap:"wrap",marginTop:16}}>
      <Btn onClick={close} disabled={busy}>Cancel</Btn>
      <Btn primary onClick={save} disabled={busy}>{busy?"Saving…":"Save changes"}</Btn>
    </div>
  </Modal>);
}

const PASTE0={open:false,title:"",lender:"",doc_type:"Email / AE note",programs:"",effective_date:"",notes:"",text:"",busy:false,err:"",ok:""};
// supOlder: "Mark the older <lender> rate sheet(s) superseded" (offered, pre-checked, for a rate sheet)
const BATCH0={lender:"",doc_type:"Guideline",programs:"",effective_date:"",notes:"",supOlder:true};
const thStyle={textAlign:"left",padding:"6px 8px",fontSize:10.5,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.6,borderBottom:"1px solid "+C.border,whiteSpace:"nowrap"};
const tdStyle={padding:"9px 8px",verticalAlign:"top",color:C.text};

// A ready row with `error` set is searchable but has scanned pages that
// couldn't be transcribed (kb.js puts the warning there) — "Retry pages".
const hasGaps=function(d){return d.status==="ready"&&!!d.error;};
// 'processing' with no heartbeat for STALE_MS and not running in this tab →
// its tab closed (or crashed / slept) mid-ingest.
const isStalled=function(d,busy){
  if(d.status==="ready"||d.status==="error"||busy)return false;
  const t=Date.parse(d.updated_at||d.created_at||"");
  return Number.isFinite(t)&&Date.now()-t>STALE_MS;
};
function statusBadge(d,stalled){
  if(d.status==="ready")return hasGaps(d)?<span title={d.error}><Badge label="Ready · gaps" color={C.orange} bg={C.orangeBg}/></span>:<Badge label="Ready" color={C.green} bg={C.greenBg}/>;
  if(d.status==="error")return <span title={d.error||""}><Badge label="Error" color={C.red} bg={C.redBg}/></span>;
  if(stalled)return <span title="This upload stopped partway (its browser tab was closed or lost its connection). Retry picks up where it left off."><Badge label="Stalled" color={C.red} bg={C.redBg}/></span>;
  return <Badge label="Processing" color={C.orange} bg={C.orangeBg}/>;
}
function ProgramChips({list}){
  if(!list||!list.length)return null;
  return(<div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:4}}>
    {list.map(function(p,i){return <span key={i} style={{fontSize:10.5,fontWeight:700,color:C.text,background:C.surfaceAlt,border:"1px solid "+C.border,borderRadius:6,padding:"1px 6px"}}>{p}</span>;})}
  </div>);
}

// The ingest queue's card. It subscribes to the queue itself, so a progress
// tick ("Reading text: page 212 of 400") re-renders only this card — not the
// documents table — and nothing at all while the Library is off screen (it
// catches up when it's shown again). The upload itself never pauses.
function QueueCard({onScreen,onOpenDoc}){
  const [queue,setQueue]=useState(function(){return IQ.items.slice();});
  const onScreenRef=useRef(onScreen);onScreenRef.current=onScreen;
  const staleRef=useRef(false);
  useEffect(function(){
    const f=function(items){if(onScreenRef.current)setQueue(items);else staleRef.current=true;};
    IQ.subs.add(f);setQueue(IQ.items.slice());
    return function(){IQ.subs.delete(f);};
  },[]);
  useEffect(function(){if(onScreen&&staleRef.current){staleRef.current=false;setQueue(IQ.items.slice());}},[onScreen]);
  if(!queue.length)return null;
  const activeQ=queue.filter(function(it){return it.state==="waiting"||it.state==="running";}).length;
  const doneQ=queue.filter(function(it){return it.state==="done";}).length;
  return(<div style={card}>
    <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:4}}>
      <div style={secTitle}>Adding to your library</div>
      <span style={{fontSize:12,color:C.textDim}}>{activeQ?activeQ+" to go":"All done"}</span>
      <span style={{flex:1}}/>
      {doneQ>0&&<Btn small onClick={iqClearDone}>Clear finished</Btn>}
    </div>
    {activeQ>0&&<div style={{fontSize:12,color:C.textDim,marginBottom:10,lineHeight:1.5}}>One file at a time. You can switch screens — it keeps going as long as this browser tab stays open.</div>}
    <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:activeQ?0:8}}>
      {queue.map(function(it){
        const err=it.state==="error",done=it.state==="done",run=it.state==="running";
        const gaps=done&&it.doc&&hasGaps(it.doc);
        return(<div key={it.qid} style={{padding:"10px 12px",border:"1px solid "+(err?C.red+"55":C.border),borderRadius:10,background:err?C.redBg:C.surfaceAlt}}>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            {run?<Spinner/>:done?<span style={{color:C.green,display:"flex"}}>{Ic.chk}</span>:err?<span style={{color:C.red,display:"flex"}}>{Ic.warn}</span>:<span style={{color:C.textDim,display:"flex"}}>{Ic.clock}</span>}
            <span style={{fontWeight:800,fontSize:13,color:C.text,minWidth:0,overflowWrap:"anywhere",flex:"1 1 160px"}}>{it.meta.title}</span>
            <span style={{fontSize:11.5,color:C.textDim}}>{[it.meta.lender,qSize(it)?fmtSize(qSize(it)):""].filter(Boolean).join(" · ")}</span>
            {done&&it.doc&&it.doc.id&&<Btn small onClick={function(){onOpenDoc(it.doc);}}>Open</Btn>}
            {gaps&&it.doc.file_path&&<Btn small primary onClick={function(){iqResumeDoc(it.doc);}}>Retry pages</Btn>}
            {err&&(it.file||it.resume)&&<Btn small primary onClick={function(){iqRetry(it.qid);}}>Retry</Btn>}
            {(err||it.state==="waiting"||done)&&<button onClick={function(){iqRemove(it.qid);}} aria-label={"Remove "+it.meta.title+" from the list"} title="Remove from list"
              style={{width:28,height:28,borderRadius:8,border:"1px solid "+C.border,background:"transparent",color:C.textDim,cursor:"pointer",flexShrink:0}}>✕</button>}
          </div>
          <div style={{fontSize:12,color:err?C.red:done?C.green:C.textMuted,marginTop:4,fontWeight:err||done?700:500,lineHeight:1.45,overflowWrap:"anywhere"}}>{stageText(it)}</div>
          {done&&it.note&&<div style={{fontSize:11.5,color:it.noteErr?C.orange:C.green,marginTop:3,fontWeight:700,lineHeight:1.45,overflowWrap:"anywhere"}}>{it.note}</div>}
          {gaps&&<div style={{fontSize:11.5,color:C.orange,marginTop:3,fontWeight:700,lineHeight:1.45,overflowWrap:"anywhere"}}>{it.doc.error}</div>}
          {run&&<div style={{height:5,borderRadius:3,background:C.cream2,marginTop:7,overflow:"hidden"}}><div style={{height:5,borderRadius:3,background:C.accent,width:pctOf(it)+"%",transition:"width .3s ease"}}/></div>}
          {err&&qName(it)&&<div style={{fontSize:11.5,color:C.textMuted,marginTop:4}}>{qName(it)}{it.resume?" — Retry picks up where it stopped":""}</div>}
        </div>);
      })}
    </div>
  </div>);
}

// Document ids the queue is working on (or about to), as one string, so the
// table only re-renders when that set changes — not on every progress tick.
function busyKeyOf(items){
  return items.filter(function(it){return it.docId&&(it.state==="waiting"||it.state==="running");}).map(function(it){return it.docId;}).sort().join(",");
}

// onScreen: the Library view is visible (either tab); tabShown: this tab is.
// readOnly: browse, search and open only (Max — staffAccess().uploadLibrary
// is false). No add/paste box, no queue, no superseded switch, no
// Retry/Edit/Delete. The database refuses those writes for him anyway
// (supabase/2026-10-01-max-limited-access.sql); this keeps them off screen.
function LibraryTab({docs,setDocs,reloadDocs,docsErr,onOpen,evictDoc,narrow,onScreen,tabShown,readOnly}){
  const [staged,setStaged]=useState([]);          // [{sid,file,title}] picked, not yet queued
  const [batch,setBatch]=useState(BATCH0);
  const [pickMsg,setPickMsg]=useState("");
  const [drag,setDrag]=useState(false);
  const [busyKey,setBusyKey]=useState(function(){return busyKeyOf(IQ.items);});
  const [paste,setPaste]=useState(function(){return loadPasteDraft(PASTE0);});
  const [flt,setFlt]=useState({lender:"",type:"",q:""});
  const [srch,setSrch]=useState({q:"",busy:false,err:"",rows:null,ran:"",terms:[]});
  const [edit,setEdit]=useState(null);
  const [del,setDel]=useState(null);              // {doc,busy,err}
  const [rowErr,setRowErr]=useState("");
  const [refreshing,setRefreshing]=useState(false);
  const fileRef=useRef(null);
  const dragDepth=useRef(0);
  const pasteRef=useRef(paste);pasteRef.current=paste;

  useEffect(function(){
    const f=function(items){setBusyKey(busyKeyOf(items));};
    IQ.subs.add(f);setBusyKey(busyKeyOf(IQ.items));
    return function(){IQ.subs.delete(f);};
  },[]);
  // A file dropped just outside the zone would make the browser navigate to
  // it (and away from the CRM) — swallow stray file drops while this is ON
  // SCREEN only: the view stays mounted behind other screens, and a
  // window-level preventDefault there would break their file drops.
  useEffect(function(){
    if(!onScreen)return;
    const stray=function(e){const t=e.dataTransfer&&e.dataTransfer.types;if(t&&Array.prototype.indexOf.call(t,"Files")>=0)e.preventDefault();};
    window.addEventListener("dragover",stray);window.addEventListener("drop",stray);
    return function(){window.removeEventListener("dragover",stray);window.removeEventListener("drop",stray);};
  },[onScreen]);
  // The paste box's draft is kept on this device (debounced; flushed on unmount).
  // (Read-only: there is no paste box, so nothing to keep — and nothing written.)
  useEffect(function(){
    if(readOnly)return;
    const t=setTimeout(function(){savePasteDraft(pasteRef.current);},NOTE_SAVE_MS);
    return function(){clearTimeout(t);};
  },[readOnly,paste.title,paste.lender,paste.doc_type,paste.programs,paste.effective_date,paste.notes,paste.text]);
  useEffect(function(){return function(){if(!readOnly)savePasteDraft(pasteRef.current);};},[]);// eslint-disable-line react-hooks/exhaustive-deps

  const byFileName=useMemo(function(){const m=new Map();(docs||[]).forEach(function(d){if(d.file_name)m.set(String(d.file_name).toLowerCase(),d);});return m;},[docs]);
  const totals=useMemo(function(){
    const o={docs:0,pages:0,processing:0,error:0,superseded:0};
    (docs||[]).forEach(function(d){o.docs++;o.pages+=Number(d.page_count)||0;if(d.status==="error")o.error++;else if(d.status!=="ready")o.processing++;if(d.superseded)o.superseded++;});
    return o;
  },[docs]);
  const shown=useMemo(function(){
    const q=flt.q.trim().toLowerCase();
    return (docs||[]).filter(function(d){
      if(flt.lender&&d.lender!==flt.lender)return false;
      if(flt.type&&d.doc_type!==flt.type)return false;
      if(!q)return true;
      return [d.title,d.file_name,(d.programs||[]).join(" ")].join(" ").toLowerCase().indexOf(q)>=0;
    });
  },[docs,flt]);

  // ── staging ──
  const addFiles=function(list){
    const arr=Array.from(list||[]);
    if(!arr.length)return;
    const ok=[],bad=[],empty=[];
    arr.forEach(function(f){if(!OK_EXT.has(extOf(f.name)))bad.push(f.name);else if(!f.size)empty.push(f.name);else ok.push(f);});
    setStaged(function(prev){
      const seen=new Set(prev.map(function(s){return s.file.name+"|"+s.file.size;}));
      return prev.concat(ok.filter(function(f){return !seen.has(f.name+"|"+f.size);}).map(function(f){return {sid:uid(),file:f,title:baseName(f.name)};}));
    });
    const msgs=[];
    if(bad.length)msgs.push("Skipped "+bad.length+" file"+(bad.length===1?"":"s")+" that can't be read ("+bad.slice(0,3).join(", ")+(bad.length>3?"…":"")+"). Use PDF, PNG, JPG, WEBP, Word (.docx), Excel (.xlsx), TXT, MD or CSV.");
    if(empty.length)msgs.push("Skipped "+empty.length+" empty file"+(empty.length===1?"":"s")+".");
    setPickMsg(msgs.join(" "));
  };
  const openPicker=function(){if(fileRef.current)fileRef.current.click();};
  const setB=function(p){setBatch(function(b){return Object.assign({},b,p);});};
  // A new rate sheet replaces the same lender's older one(s): the current
  // "Rate sheet" documents from that lender with an older effective date.
  const isRateSheet=batch.doc_type===KB_RATE_SHEET;
  const olderSheets=useMemo(function(){
    return isRateSheet&&batch.lender?olderRateSheets(docs||[],batch.lender,batch.effective_date):[];
  },[docs,isRateSheet,batch.lender,batch.effective_date]);
  const startIngest=function(){
    if(!batch.lender||!staged.length)return;
    const programs=parsePrograms(batch.programs);
    const sup=isRateSheet&&batch.supOlder?olderSheets.map(function(d){return {id:d.id,title:d.title,lender:d.lender};}):[];
    iqAdd(staged.map(function(s){return {qid:uid(),file:s.file,state:"waiting",progress:null,error:"",resume:null,supersede:sup.length?sup:null,
      meta:{title:(s.title||"").trim()||baseName(s.file.name),lender:batch.lender,doc_type:batch.doc_type||"Guideline",programs:programs,effective_date:batch.effective_date||null,notes:batch.notes.trim()}};}));
    setStaged([]);setPickMsg("");
    // keep lender + type for the next batch (usually the same lender); clear the rest
    setB({programs:"",effective_date:"",notes:"",supOlder:true});
  };

  // ── paste text / AE email ──
  const setP=function(p){setPaste(function(x){return Object.assign({},x,p);});};
  const savePaste=async function(){
    const p=paste;
    if(!p.title.trim()||!p.lender||!p.text.trim()){setP({err:"Title, lender and the text itself are required.",ok:""});return;}
    setP({busy:true,err:"",ok:""});
    try{
      const row=await addTextNote({title:p.title.trim(),lender:p.lender,doc_type:p.doc_type||"Email / AE note",programs:parsePrograms(p.programs),effective_date:p.effective_date||null,notes:p.notes.trim()},p.text);
      if(row&&row.id)setDocs(function(prev){return upsertDoc(prev,row);});else reloadDocs();
      const n=row&&row.page_count;
      savePasteDraft(PASTE0);   // saved — the kept draft goes now, not 0.7 s later
      setPaste(Object.assign({},PASTE0,{open:true,lender:p.lender,doc_type:p.doc_type,ok:"Added “"+p.title.trim()+"”"+(n?" — "+n+" page"+(n===1?"":"s"):"")+". It's searchable now."}));
    }catch(e){
      // a half-saved note leaves an 'error' row (err.doc) — show it in the list so it can be deleted
      if(e&&e.doc&&e.doc.id)setDocs(function(prev){return upsertDoc(prev,e.doc);});
      setP({busy:false,err:errMsg(e,"Couldn't save this note.")});
    }
  };

  // ── documents ──
  const openDoc=function(d){onOpen({docId:d.id,page:1,meta:docMeta(d)});};
  const toggleSup=function(d){
    const next=!d.superseded;setRowErr("");
    const flip=function(val){setDocs(function(prev){return (prev||[]).map(function(x){return x.id===d.id?Object.assign({},x,{superseded:val}):x;});});};
    flip(next);
    updateDocument(d.id,{superseded:next}).then(function(row){if(row&&row.id)setDocs(function(prev){return upsertDoc(prev,row);});},
      function(e){flip(!next);setRowErr("Couldn't change “"+d.title+"”: "+errMsg(e));});
  };
  const doDelete=async function(){
    if(!del)return;
    const d=del.doc;
    setDel({doc:d,busy:true,err:""});
    try{
      await deleteDocument(d);
      evictDoc(d.id);
      setDocs(function(prev){return (prev||[]).filter(function(x){return x.id!==d.id;});});
      setDel(null);
    }catch(e){setDel({doc:d,busy:false,err:errMsg(e,"Couldn't delete it.")});}
  };
  const refresh=function(){setRefreshing(true);Promise.resolve(reloadDocs()).then(function(){setRefreshing(false);},function(){setRefreshing(false);});};

  // ── search inside documents ──
  const runSearch=async function(){
    const q=srch.q.trim();
    if(!q||srch.busy)return;
    const terms=termsOf(q);
    setSrch(function(s){return Object.assign({},s,{busy:true,err:"",ran:q,terms:terms});});
    try{
      const rows=await searchPages(q,{lenders:flt.lender?[flt.lender]:undefined,includeSuperseded:true,limit:30});
      setSrch(function(s){return s.ran===q?Object.assign({},s,{busy:false,rows:Array.isArray(rows)?rows:[]}):s;});
    }catch(e){setSrch(function(s){return Object.assign({},s,{busy:false,rows:null,err:errMsg(e,"The search didn't work. Try again.")});});}
  };
  const openHit=function(r){
    onOpen({docId:r.doc_id,page:r.page,quote:snippetOf(r.text,srch.terms,400),quotePage:r.page,quoteLabel:"Search match",terms:srch.terms,pageText:r.text,pageTextPage:r.page,
      meta:{title:r.title,lender:r.lender,docType:r.doc_type,effectiveDate:r.effective_date,superseded:!!r.superseded}});
  };

  // Rows this tab's queue is working on (or about to) — never "stalled", and
  // not deletable mid-run.
  const busyIds=useMemo(function(){return new Set(busyKey?busyKey.split(","):[]);},[busyKey]);
  // What a row offers when it didn't finish: resume it (kept record + pages).
  // A Word / Excel file can also be re-read from its stored original at any
  // time (replaces its pages — fixes text the bulk importer mangled); the old
  // binary .doc / .xls can't be read here at all, so they get no button.
  const retryLabel=function(d,stalled){
    if(readOnly||!d.file_path||busyIds.has(d.id))return "";
    const office=officeKind(d);
    if(office==="legacy")return "";
    if(hasGaps(d))return "Retry pages";
    if(d.status==="error"||stalled)return "Retry";
    if(office&&d.status==="ready")return "Re-read text";
    return "";
  };
  const retryTip=function(rl){return rl==="Re-read text"?"Read this Word/Excel file's text again from the stored original and replace its pages":undefined;};
  const closePaste=function(){savePasteDraft(PASTE0);setPaste(PASTE0);};

  return(<div>
    {readOnly&&<div style={{padding:"10px 14px",background:C.surfaceAlt,border:"1px solid "+C.border,borderRadius:10,fontSize:12.5,color:C.textMuted,marginBottom:16,lineHeight:1.5}}>
      Browse, search and open the lender documents here. Adding, editing and removing documents is Reece's.
    </div>}

    {/* ── add documents ── */}
    {!readOnly&&<div style={card}>
      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:12}}>
        <div style={secTitle}>Add documents</div>
        <span style={{fontSize:12,color:C.textDim,flex:"1 1 220px"}}>Guidelines, bulletins, rate sheets, matrices, FAQs — PDFs, scans, screenshots, Word/Excel or text files.</span>
        <Btn small onClick={function(){setP({open:!paste.open,ok:"",err:""});}}>{paste.open?"Close paste box":"Paste text / AE email"}</Btn>
      </div>
      <div role="button" tabIndex={0} aria-label="Add files: drop them here or press Enter to choose"
        onClick={openPicker}
        onKeyDown={function(e){if(e.key==="Enter"||e.key===" "){e.preventDefault();openPicker();}}}
        onDragEnter={function(e){e.preventDefault();dragDepth.current++;setDrag(true);}}
        onDragOver={function(e){e.preventDefault();if(e.dataTransfer)e.dataTransfer.dropEffect="copy";}}
        onDragLeave={function(e){e.preventDefault();dragDepth.current=Math.max(0,dragDepth.current-1);if(!dragDepth.current)setDrag(false);}}
        onDrop={function(e){e.preventDefault();dragDepth.current=0;setDrag(false);addFiles(e.dataTransfer&&e.dataTransfer.files);}}
        style={{border:"2px dashed "+(drag?C.accent:C.border),background:drag?C.accentGlow:C.surfaceAlt,borderRadius:12,padding:"26px 16px",textAlign:"center",cursor:"pointer",transition:"background .15s, border-color .15s"}}>
        <div style={{color:drag?C.accent:C.textMuted,display:"flex",justifyContent:"center",marginBottom:6}}>{Ic.upload}</div>
        <div style={{fontSize:14,fontWeight:800,color:C.text}}>{drag?"Drop to add":"Drop files here, or click to choose"}</div>
        <div style={{fontSize:12,color:C.textDim,marginTop:4}}>PDF · PNG · JPG · WEBP · DOCX · XLSX · TXT · MD · CSV — several at once is fine. Scanned pages are read with AI.</div>
      </div>
      <input ref={fileRef} type="file" multiple accept={ACCEPT} style={{display:"none"}} onChange={function(e){addFiles(e.target.files);e.target.value="";}}/>
      {pickMsg&&<div style={{marginTop:10,fontSize:12,color:C.orange,fontWeight:700}}>{pickMsg}</div>}

      {staged.length>0&&<div style={{marginTop:16}}>
        <div style={{...label11,marginBottom:8}}>{staged.length} file{staged.length===1?"":"s"} ready — check the titles</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {staged.map(function(s){const dup=byFileName.get(s.file.name.toLowerCase());return(
            <div key={s.sid} style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",padding:"8px 10px",border:"1px solid "+C.border,borderRadius:10,background:C.card}}>
              <div style={{flex:"1 1 240px",minWidth:0}}>
                <Inp value={s.title} placeholder="Title" onChange={function(e){const val=e.target.value;setStaged(function(p){return p.map(function(x){return x.sid===s.sid?Object.assign({},x,{title:val}):x;});});}} style={{padding:"8px 10px",minHeight:36,fontSize:13}}/>
              </div>
              <span style={{fontSize:11.5,color:C.textDim,overflowWrap:"anywhere",minWidth:0}}>{s.file.name} · {fmtSize(s.file.size)}</span>
              <button onClick={function(){setStaged(function(p){return p.filter(function(x){return x.sid!==s.sid;});});}} aria-label={"Remove "+s.file.name} title="Remove"
                style={{width:28,height:28,borderRadius:8,border:"1px solid "+C.border,background:"transparent",color:C.textDim,cursor:"pointer",flexShrink:0}}>✕</button>
              {dup&&<div style={{flexBasis:"100%",fontSize:11.5,color:C.orange,fontWeight:700}}>Already in your library as “{dup.title}”{dup.created_at?" (added "+fmtDay(dup.created_at)+")":""} — adding it again makes a second copy.</div>}
            </div>);})}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12,marginTop:14}}>
          <Field label="Lender" req><Sel value={batch.lender} onChange={function(e){setB({lender:e.target.value});}} options={LENDER_OPTS}/></Field>
          <Field label="Document type"><Sel value={batch.doc_type} onChange={function(e){setB({doc_type:e.target.value});}} options={TYPE_OPTS}/></Field>
          <Field label="Programs" hint="Comma-separated, e.g. HomeSafe Standard, HECM"><Inp value={batch.programs} onChange={function(e){setB({programs:e.target.value});}} placeholder="Optional"/></Field>
          <Field label="Effective date"><Inp type="date" value={batch.effective_date} onChange={function(e){setB({effective_date:e.target.value});}}/></Field>
        </div>
        {olderSheets.length>0&&<div style={{marginTop:12,padding:"10px 12px",border:"1px solid "+C.border,borderRadius:10,background:C.surfaceAlt}}>
          <label style={{display:"flex",alignItems:"flex-start",gap:8,cursor:"pointer",fontSize:13,fontWeight:700,color:C.text}}>
            <input type="checkbox" checked={!!batch.supOlder} onChange={function(e){setB({supOlder:e.target.checked});}} style={{width:16,height:16,marginTop:1,cursor:"pointer",flexShrink:0}}/>
            <span>Mark the older {batch.lender} rate sheet{olderSheets.length===1?"":"s"} superseded</span>
          </label>
          <div style={{fontSize:11.5,color:C.textDim,marginTop:4,marginLeft:24,lineHeight:1.5,overflowWrap:"anywhere"}}>
            {olderSheets.map(function(d){return "“"+d.title+"”"+(d.effective_date?" · eff. "+fmtEff(d.effective_date):"");}).join("; ")}
            {" — "}answers then come from the new sheet; done once this upload finishes.
          </div>
        </div>}
        {isRateSheet&&batch.lender&&batch.lender!=="Other"&&!batch.effective_date&&<div style={{marginTop:10,fontSize:12,color:C.textDim}}>Add the sheet's effective date so an older {batch.lender} rate sheet can be retired (marked superseded).</div>}
        <div style={{marginTop:12}}><Field label="Notes (applies to every file in this batch)"><textarea value={batch.notes} onChange={function(e){setB({notes:e.target.value});}} placeholder="Optional — e.g. where it came from, what changed" style={{...taStyle,minHeight:56}}/></Field></div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginTop:14}}>
          <Btn primary onClick={startIngest} disabled={!batch.lender}>{Ic.upload} Add {staged.length} file{staged.length===1?"":"s"} to the library</Btn>
          <Btn onClick={function(){setStaged([]);setPickMsg("");}}>Cancel</Btn>
          {!batch.lender&&<span style={{fontSize:12,color:C.textDim}}>Pick the lender first.</span>}
        </div>
      </div>}
    </div>}

    {/* ── paste text / AE email ── */}
    {!readOnly&&paste.open&&<div style={card}>
      <div style={secTitle}>Paste text / AE email</div>
      <div style={{fontSize:12,color:C.textDim,marginTop:3,marginBottom:12,lineHeight:1.5}}>For things that aren't files — an AE's email, a scenario-desk answer, a policy note. It's searchable and citable like any document.</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
        <div style={{gridColumn:"1 / -1"}}><Field label="Title" req><Inp value={paste.title} onChange={function(e){setP({title:e.target.value});}} placeholder="e.g. FOA AE — condo approval workaround, 9/30"/></Field></div>
        <Field label="Lender" req><Sel value={paste.lender} onChange={function(e){setP({lender:e.target.value});}} options={LENDER_OPTS}/></Field>
        <Field label="Document type"><Sel value={paste.doc_type} onChange={function(e){setP({doc_type:e.target.value});}} options={TYPE_OPTS}/></Field>
        <Field label="Programs" hint="Comma-separated"><Inp value={paste.programs} onChange={function(e){setP({programs:e.target.value});}} placeholder="Optional"/></Field>
        <Field label="Effective date"><Inp type="date" value={paste.effective_date} onChange={function(e){setP({effective_date:e.target.value});}}/></Field>
      </div>
      <div style={{marginTop:12}}><Field label="Text" req><textarea value={paste.text} onChange={function(e){setP({text:e.target.value});}} placeholder="Paste the email or note here" style={{...taStyle,minHeight:160}}/></Field></div>
      <div style={{marginTop:12}}><Field label="Notes"><textarea value={paste.notes} onChange={function(e){setP({notes:e.target.value});}} placeholder="Optional" style={{...taStyle,minHeight:50}}/></Field></div>
      {paste.err&&<div style={{...errBox,marginTop:12}}>{paste.err}</div>}
      {paste.ok&&<div style={{marginTop:12,padding:"10px 14px",background:C.greenBg,color:C.green,borderRadius:8,fontSize:12.5,fontWeight:700}}>{paste.ok}</div>}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginTop:14}}>
        <Btn primary onClick={savePaste} disabled={paste.busy}>{paste.busy?<><Spinner color="#fff"/>Saving…</>:"Save to library"}</Btn>
        <Btn onClick={closePaste} disabled={paste.busy}>Close</Btn>
        {pasteHasDraft(paste)&&!paste.busy&&<span style={{fontSize:11,color:C.textDim}}>Draft kept on this device until you save or close it.</span>}
      </div>
    </div>}

    {/* ── ingest queue ── */}
    {!readOnly&&<QueueCard onScreen={tabShown} onOpenDoc={openDoc}/>}

    {/* ── search inside documents ── */}
    <div style={card}>
      <div style={secTitle}>Search inside documents</div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10,alignItems:"center"}}>
        <div style={{flex:"1 1 240px",minWidth:0}}>
          <Inp value={srch.q} onChange={function(e){const val=e.target.value;setSrch(function(s){return Object.assign({},s,{q:val});});}} onKeyDown={function(e){if(e.key==="Enter")runSearch();}} placeholder={"Words or phrases — e.g. \"non-borrowing spouse\" or NBS"}/>
        </div>
        <Btn primary onClick={runSearch} disabled={srch.busy||!srch.q.trim()}>{srch.busy?<Spinner color="#fff"/>:Ic.search} Search</Btn>
        {(srch.rows||srch.err)&&<Btn onClick={function(){setSrch({q:"",busy:false,err:"",rows:null,ran:"",terms:[]});}}>Clear</Btn>}
      </div>
      {flt.lender&&<div style={{fontSize:11.5,color:C.textDim,marginTop:6}}>Only searching {flt.lender} (from the lender filter on the documents list).</div>}
      {srch.err&&<div style={{...errBox,marginTop:10}}>{srch.err}</div>}
      {srch.rows&&!srch.busy&&<div style={{marginTop:12}}>
        <div style={{fontSize:12,color:C.textMuted,marginBottom:6}}>{srch.rows.length
          ?srch.rows.length+" matching page"+(srch.rows.length===1?"":"s")+" for “"+srch.ran+"”"
          :"No pages matched “"+srch.ran+"”. Try fewer or different words — an abbreviation as well as the full phrase (e.g. NBS and non-borrowing spouse)."}</div>
        <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:460,overflowY:"auto"}}>
          {srch.rows.map(function(r){return(
            <button key={r.doc_id+":"+r.page} onClick={function(){openHit(r);}} title="Show this page"
              style={{display:"block",width:"100%",textAlign:"left",padding:"10px 12px",borderRadius:10,border:"1px solid "+C.border,background:C.surfaceAlt,cursor:"pointer",fontFamily:FONT,boxSizing:"border-box"}}>
              <span style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                <span style={{fontSize:13,fontWeight:800,color:C.text,overflowWrap:"anywhere"}}>{r.title||"Untitled"}</span>
                <span style={{fontSize:12,fontWeight:800,color:C.accentLight}}>p.{r.page}</span>
                <span style={{fontSize:11.5,color:C.textDim}}>{[r.lender,r.doc_type,r.effective_date?"eff. "+fmtEff(r.effective_date):""].filter(Boolean).join(" · ")}</span>
                {r.superseded&&<Badge label="superseded" color={C.orange} bg={C.orangeBg}/>}
              </span>
              <span style={{display:"block",fontSize:12.5,color:C.textMuted,marginTop:4,lineHeight:1.5,overflowWrap:"anywhere"}}><Highlight text={snippetOf(r.text,srch.terms,200)} terms={srch.terms}/></span>
            </button>);})}
        </div>
      </div>}
    </div>

    {/* ── documents ── */}
    <div style={card}>
      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:12}}>
        <div style={secTitle}>Documents</div>
        {Array.isArray(docs)&&<span style={{fontSize:12,color:C.textDim}}>
          {totals.docs} document{totals.docs===1?"":"s"} · {totals.pages.toLocaleString()} page{totals.pages===1?"":"s"}
          {totals.processing?" · "+totals.processing+" processing":""}{totals.error?" · "+totals.error+" with errors":""}{totals.superseded?" · "+totals.superseded+" superseded":""}
        </span>}
        <span style={{flex:1}}/>
        <Btn small onClick={refresh} disabled={refreshing}>{refreshing?"Refreshing…":"Refresh"}</Btn>
      </div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:12}}>
        <Sel value={flt.lender} onChange={function(e){const v=e.target.value;setFlt(function(f){return Object.assign({},f,{lender:v});});}} options={[{value:"",label:"All lenders"}].concat(LENDER_OPTS.slice(1))}/>
        <Sel value={flt.type} onChange={function(e){const v=e.target.value;setFlt(function(f){return Object.assign({},f,{type:v});});}} options={[{value:"",label:"All types"}].concat(TYPE_OPTS)}/>
        <div style={{flex:"1 1 200px",minWidth:0}}><Inp value={flt.q} onChange={function(e){const v=e.target.value;setFlt(function(f){return Object.assign({},f,{q:v});});}} placeholder="Filter by title"/></div>
      </div>
      {rowErr&&<div style={{...errBox,marginBottom:10}}>{rowErr}</div>}

      {!Array.isArray(docs)
        ?(docsErr
          ?<div style={{padding:"20px",textAlign:"center",color:C.textMuted,fontSize:13}}>Your documents couldn't be loaded — see the message at the top of the page.</div>
          :<div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:"24px",color:C.textMuted,fontSize:13}}><Spinner/>Loading your library…</div>)
      :docs.length===0
        ?<div style={{padding:"26px 20px",textAlign:"center",border:"1px dashed "+C.border,borderRadius:12,color:C.textDim,fontSize:13,lineHeight:1.55}}>{readOnly?"No lender documents have been added yet.":"Nothing here yet. Drop your first lender guideline or bulletin in the box above."}</div>
      :shown.length===0
        ?<div style={{padding:"22px 20px",textAlign:"center",border:"1px dashed "+C.border,borderRadius:12,color:C.textDim,fontSize:13}}>No documents match these filters.</div>
      :narrow
        // Phone: one card per document.
        ?<div style={{display:"flex",flexDirection:"column",gap:8}}>
          {shown.map(function(d){const busy=busyIds.has(d.id),stalled=isStalled(d,busy),rl=retryLabel(d,stalled);return(
            <div key={d.id} style={{border:"1px solid "+C.border,borderRadius:10,padding:"10px 12px",background:d.superseded?C.surfaceAlt:C.card}}>
              <button onClick={function(){openDoc(d);}} style={{...linkBtn,fontSize:14,color:C.text,textAlign:"left",overflowWrap:"anywhere"}}>{d.title||"Untitled"}</button>
              <div style={{fontSize:12,color:C.textMuted,marginTop:3}}>{[d.lender,d.doc_type,d.effective_date?"eff. "+fmtEff(d.effective_date):"",d.page_count?d.page_count+" page"+(d.page_count===1?"":"s"):""].filter(Boolean).join(" · ")}</div>
              <ProgramChips list={d.programs}/>
              {d.error&&(d.status==="error"||hasGaps(d))&&<div style={{fontSize:11.5,color:hasGaps(d)?C.orange:C.red,marginTop:4,overflowWrap:"anywhere"}}>{d.error}</div>}
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginTop:8}}>
                {statusBadge(d,stalled)}
                {readOnly?(d.superseded&&<Badge label="superseded" color={C.orange} bg={C.orangeBg}/>):<span style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12,color:d.superseded?C.orange:C.textMuted,fontWeight:700}}><Switch on={d.superseded} onClick={function(){toggleSup(d);}} title={d.superseded?"Mark as current":"Mark as superseded"}/>Superseded</span>}
                <span style={{flex:1}}/>
                {rl&&<span title={retryTip(rl)} style={{display:"inline-flex"}}><Btn small primary={rl!=="Re-read text"} onClick={function(){iqResumeDoc(d);}}>{rl}</Btn></span>}
                <Btn small onClick={function(){openDoc(d);}} disabled={!d.page_count&&!d.file_path}>Open</Btn>
                {!readOnly&&<Btn small onClick={function(){setEdit(d);}}>Edit</Btn>}
                {!readOnly&&<Btn small onClick={function(){setDel({doc:d,busy:false,err:""});}} disabled={busy} style={{color:C.red}}>Delete</Btn>}
              </div>
            </div>);})}
        </div>
        // Desktop: table (scrolls inside its card if the window is tight).
        :<div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5,minWidth:860}}>
            <thead><tr>{["Title","Lender","Type","Effective","Pages","Status","Superseded",""].map(function(h,i){return <th key={i} style={thStyle}>{h}</th>;})}</tr></thead>
            <tbody>{shown.map(function(d){const busy=busyIds.has(d.id),stalled=isStalled(d,busy),rl=retryLabel(d,stalled);return(
              <tr key={d.id} style={{borderBottom:"1px solid "+C.border,background:d.superseded?C.surfaceAlt:"transparent"}}>
                <td style={{...tdStyle,minWidth:260}}>
                  <button onClick={function(){openDoc(d);}} title="Open page 1" style={{...linkBtn,fontSize:13,color:C.text,textAlign:"left",overflowWrap:"anywhere"}}>{d.title||"Untitled"}</button>
                  <div style={{fontSize:11,color:C.textDim,marginTop:2,overflowWrap:"anywhere"}}>{[d.file_name||(d.file_path?"":"Pasted text"),d.size_bytes?fmtSize(d.size_bytes):"",d.created_at?"added "+fmtDay(d.created_at):""].filter(Boolean).join(" · ")}</div>
                  <ProgramChips list={d.programs}/>
                  {d.error&&(d.status==="error"||hasGaps(d))&&<div style={{fontSize:11.5,color:hasGaps(d)?C.orange:C.red,marginTop:4,overflowWrap:"anywhere"}}>{d.error}</div>}
                  {d.notes&&<div style={{fontSize:11.5,color:C.textMuted,marginTop:4,whiteSpace:"pre-wrap",overflowWrap:"anywhere"}}>{d.notes}</div>}
                </td>
                <td style={{...tdStyle,whiteSpace:"nowrap"}}>{d.lender}</td>
                <td style={tdStyle}>{d.doc_type}</td>
                <td style={{...tdStyle,whiteSpace:"nowrap"}}>{fmtEff(d.effective_date)||<span style={{color:C.textDim}}>—</span>}</td>
                <td style={tdStyle}>{d.page_count||<span style={{color:C.textDim}}>—</span>}</td>
                <td style={tdStyle}>{statusBadge(d,stalled)}</td>
                <td style={tdStyle}>{readOnly?(d.superseded?<Badge label="superseded" color={C.orange} bg={C.orangeBg}/>:<span style={{color:C.textDim}}>—</span>):<Switch on={d.superseded} onClick={function(){toggleSup(d);}} title={d.superseded?"Superseded — click to mark as current":"Current — click to mark as superseded"}/>}</td>
                <td style={{...tdStyle,textAlign:"right",whiteSpace:"nowrap"}}>
                  <div style={{display:"inline-flex",gap:6}}>
                    {rl&&<span title={retryTip(rl)} style={{display:"inline-flex"}}><Btn small primary={rl!=="Re-read text"} onClick={function(){iqResumeDoc(d);}}>{rl}</Btn></span>}
                    <Btn small onClick={function(){openDoc(d);}} disabled={!d.page_count&&!d.file_path}>Open</Btn>
                    {!readOnly&&<Btn small onClick={function(){setEdit(d);}}>Edit</Btn>}
                    {!readOnly&&<Btn small onClick={function(){setDel({doc:d,busy:false,err:""});}} disabled={busy} style={{color:C.red}}>Delete</Btn>}
                  </div>
                </td>
              </tr>);})}</tbody>
          </table>
        </div>}
    </div>

    {!readOnly&&edit&&<EditDocModal doc={edit} shown={onScreen} onClose={function(){setEdit(null);}} onSaved={function(row){setDocs(function(prev){return upsertDoc(prev,row);});setEdit(null);}}
      onPatched={function(row){setDocs(function(prev){return prev?prev.map(function(x){return x.id===row.id?Object.assign({},x,{notes:row.notes}):x;}):prev;});}}/>}
    {!readOnly&&del&&<Modal title="Delete this document?" onClose={del.busy||!onScreen?null:function(){setDel(null);}}>
      <div style={{fontSize:13.5,color:C.text,lineHeight:1.6}}>
        Delete <strong>“{del.doc.title||"Untitled"}”</strong>{del.doc.lender?" ("+del.doc.lender+")":""}? This removes {del.doc.file_path?"the file and ":""}all {del.doc.page_count||0} page{del.doc.page_count===1?"":"s"} from your library. Past answers that cited it keep the reference, but its pages won't open anymore. This can't be undone.
      </div>
      {del.err&&<div style={{...errBox,marginTop:12}}>{del.err}</div>}
      <div style={{display:"flex",gap:8,justifyContent:"flex-end",flexWrap:"wrap",marginTop:16}}>
        <Btn onClick={function(){setDel(null);}} disabled={del.busy}>Cancel</Btn>
        <Btn danger onClick={doDelete} disabled={del.busy}>{del.busy?"Deleting…":"Delete"}</Btn>
      </div>
    </Modal>}
  </div>);
}

// ═══════ the view ═══════
// App mounts this on the first visit and from then on only HIDES it
// (display:none) on other screens — `visible` says which — so an upload, a
// search or a half-filled form survives working elsewhere. `staff` picks
// whose conversation the Ask tab shows (one per person — src/libraryChat.js);
// an answer streams in that store whether or not this view is mounted, and
// App reads busy / finished from the store for the nav spinner, the
// "Library answering…" chip and the chat bubble's dot. Hidden, this view
// stays out of the way: no window-level key/drop handlers, no page viewer, no
// re-renders for streamed text or upload progress (both catch up when it's
// shown again). readOnly (Max): Ask works, the Library tab only browses /
// searches / opens — see LibraryTab. covered: the New Call console is open over it — the Ask tab
// then doesn't count as showing the conversation (an answer that lands is
// "new" for the chat bubble, which floats above the console).
function LenderLibrary({visible,staff,covered,readOnly}){
  const who=staff||"reece";
  const onScreen=visible!==false;
  const [tab,setTab]=useState("ask");
  const [docs,setDocs]=useState(null);           // null = loading / failed
  const [docsErr,setDocsErr]=useState(null);     // {text, setup?}
  const [retrying,setRetrying]=useState(false);
  const [viewer,setViewer]=useState(null);       // {docId,page,quote,quotePage,quoteLabel,terms,pageText,pageTextPage,srcKey,meta}
  const narrow=useNarrow(720);
  const imgCache=useRef(new Map());              // "docId:page" → {url,at,blob} (LRU)
  const inflight=useRef(new Map());
  const linkCache=useRef(new Map());             // docId → {url,at}
  const mountedRef=useRef(true);
  const docsAtRef=useRef(0);                     // when the documents list was last read

  const reloadDocs=useCallback(function(){
    docsAtRef.current=Date.now();
    return listDocuments().then(function(rows){setDocs(Array.isArray(rows)?rows:[]);setDocsErr(null);},
      function(e){setDocsErr(docsErrText(e));});
  },[]);
  useEffect(function(){reloadDocs();},[reloadDocs]);
  // Coming back to the Library: if an answer is still being written, or
  // finished while he was away, show the Ask tab (that's what the chip
  // promised); and re-read a documents list that's gone stale (the other
  // user may have added something).
  const wasOnScreen=useRef(onScreen);
  useEffect(function(){
    const was=wasOnScreen.current;wasOnScreen.current=onScreen;
    if(!onScreen||was)return;
    const chat=getChatStore(who).getSnapshot();
    if(chat.busy||(chat.fresh&&!chat.fresh.seen))setTab("ask");
    if(Date.now()-docsAtRef.current>DOCS_STALE_MS)reloadDocs();
  },[onScreen,reloadDocs,who]);
  // Ingest queue → keep the documents list current as files finish.
  useEffect(function(){
    IQ.onDoc=function(row){if(row&&row.id)setDocs(function(prev){return upsertDoc(prev,row);});else reloadDocs();};
    return function(){IQ.onDoc=null;};
  },[reloadDocs]);
  // A rate sheet applied in Settings lands in the library (and may retire an
  // older one) from outside this screen — re-read the list when that happens.
  useEffect(function(){
    const f=function(){reloadDocs();};
    window.addEventListener(KB_CHANGED_EVENT,f);
    return function(){window.removeEventListener(KB_CHANGED_EVENT,f);};
  },[reloadDocs]);
  // Rendered page images are blob URLs — free them when the view unmounts
  // (sign-out; switching screens only hides it).
  useEffect(function(){
    mountedRef.current=true;
    const cache=imgCache.current;
    return function(){
      mountedRef.current=false;
      cache.forEach(function(o){if(o&&o.blob){try{URL.revokeObjectURL(o.url);}catch(e){/* ignore */}}});
      cache.clear();
    };
  },[]);

  const getPageImage=useCallback(function(doc,page){
    const key=doc.id+":"+page,cache=imgCache.current,fl=inflight.current;
    const hit=cache.get(key);
    if(hit&&(hit.blob||Date.now()-hit.at<LINK_STALE_MS)){cache.delete(key);cache.set(key,hit);return Promise.resolve(hit.url);}
    if(fl.has(key))return fl.get(key);
    const p=Promise.resolve().then(function(){return renderPageImage(doc,page,{maxWidth:1100});}).then(function(url){
      fl.delete(key);
      if(!url)return null;
      const blob=/^blob:/.test(url);
      if(!mountedRef.current){if(blob){try{URL.revokeObjectURL(url);}catch(e){/* ignore */}}return null;}
      cache.set(key,{url:url,at:Date.now(),blob:blob});
      while(cache.size>IMG_CACHE_MAX){
        const k=cache.keys().next().value;const o=cache.get(k);cache.delete(k);
        if(o&&o.blob){try{URL.revokeObjectURL(o.url);}catch(e){/* ignore */}}
      }
      return url;
    },function(e){fl.delete(key);throw e;});
    fl.set(key,p);
    return p;
  },[]);
  const dropPageImage=useCallback(function(doc,page){
    const key=doc.id+":"+page,o=imgCache.current.get(key);
    if(o){imgCache.current.delete(key);if(o.blob){try{URL.revokeObjectURL(o.url);}catch(e){/* ignore */}}}
  },[]);
  const evictDoc=useCallback(function(docId){
    const pre=docId+":";
    Array.from(imgCache.current.keys()).forEach(function(k){
      if(k.indexOf(pre)!==0)return;
      const o=imgCache.current.get(k);imgCache.current.delete(k);
      if(o&&o.blob){try{URL.revokeObjectURL(o.url);}catch(e){/* ignore */}}
    });
    linkCache.current.delete(docId);
  },[]);
  const getLink=useCallback(function(doc,force){
    const c=linkCache.current.get(doc.id);
    if(!force&&c&&Date.now()-c.at<LINK_STALE_MS)return Promise.resolve(c);
    return getSignedUrl(doc,3600).then(function(url){
      if(!url)throw new Error("No link came back for this file.");
      const o={url:url,at:Date.now()};linkCache.current.set(doc.id,o);return o;
    });
  },[]);

  const openViewer=useCallback(function(v){setViewer(Object.assign({page:1},v));},[]);
  const closeViewer=useCallback(function(){setViewer(null);},[]);
  const setViewerPage=useCallback(function(p){setViewer(function(v){return v?Object.assign({},v,{page:Math.max(1,p)}):v;});},[]);
  const goLibrary=useCallback(function(){setTab("library");window.scrollTo(0,0);},[]);
  const retryDocs=function(){setRetrying(true);reloadDocs().then(function(){setRetrying(false);});};
  const viewerDoc=viewer&&Array.isArray(docs)?(docs.find(function(d){return d.id===viewer.docId;})||null):null;

  const tabBtn=function(k,l){const on=tab===k;return <button key={k} onClick={function(){setTab(k);}} aria-pressed={on}
    style={{padding:"9px 18px",borderRadius:10,cursor:"pointer",fontSize:13,fontWeight:800,fontFamily:FONT,border:"1px solid "+(on?C.navy:C.border),background:on?C.navy:C.card,color:on?"#fff":C.textMuted}}>{l}</button>;};

  return(<div>
    <style>{`@keyframes lf-kb-spin{to{transform:rotate(360deg)}}@keyframes lf-kb-slide{from{transform:translateX(100%)}to{transform:none}}`}</style>
    <Hdr t="Lender Library" s="Ask about your lender guidelines, bulletins and rate sheets the way you'd ask your AE — the pages behind every answer are one tap away."/>

    <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
      {tabBtn("ask","Ask")}
      {tabBtn("library","Library ("+(Array.isArray(docs)?docs.length:"…")+")")}
    </div>

    {docsErr&&<div style={{padding:"12px 14px",background:docsErr.setup?C.orangeBg:C.redBg,border:"1px solid "+(docsErr.setup?C.orange:C.red)+"40",color:docsErr.setup?C.text:C.red,borderRadius:10,fontSize:12.5,fontWeight:600,marginBottom:16,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",lineHeight:1.5}}>
      <span style={{flex:"1 1 240px",overflowWrap:"anywhere"}}>{docsErr.text}</span>
      <Btn small onClick={retryDocs} disabled={retrying}>{retrying?"Retrying…":"Retry"}</Btn>
    </div>}

    {/* Both tabs stay mounted so a streaming answer, a search or a half-filled
        form survives flipping between them. */}
    <div style={{display:tab==="ask"?"block":"none"}}>
      <AskTab staff={who} docs={docs} onOpen={openViewer} activeSrc={viewer&&viewer.srcKey} goLibrary={goLibrary}
        shown={onScreen&&tab==="ask"&&!covered} readOnly={!!readOnly}/>
    </div>
    <div style={{display:tab==="library"?"block":"none"}}>
      <LibraryTab docs={docs} setDocs={setDocs} reloadDocs={reloadDocs} docsErr={docsErr} onOpen={openViewer} evictDoc={evictDoc} narrow={narrow}
        onScreen={onScreen} tabShown={onScreen&&tab==="library"} readOnly={!!readOnly}/>
    </div>

    {/* The viewer locks page scroll and listens for keys, so it only exists
        while the Library is on screen (it reopens where it was on return). */}
    {viewer&&onScreen&&<PageViewer v={viewer} doc={viewerDoc} docsLoading={docs===null&&!docsErr} onClose={closeViewer} setPage={setViewerPage}
      getPageImage={getPageImage} dropPageImage={dropPageImage} getLink={getLink} narrow={narrow}/>}
  </div>);
}

export { LenderLibrary, asksForSources };
